import type { Editor } from 'tinymce'
import {
  INLINE_TEXT_BLOCK_SELECTOR,
  blockFontsToPin,
  fontDeclarationsOf,
  holdsInlineText,
  isBlockLevelTag,
  isHiddenByStyle,
  tallestRunFont,
  type BlockFont,
} from './blockFont'

const holdsInlineContent = (block: HTMLElement): boolean =>
  Array.from(block.childNodes).some((node) =>
    node.nodeType === Node.TEXT_NODE
      ? holdsInlineText((node as Text).data)
      : node.nodeType === Node.ELEMENT_NODE &&
        !isBlockLevelTag((node as Element).tagName),
  )

const isHiddenWithin = (el: Element, block: HTMLElement): boolean => {
  let current: Element | null = el
  while (current && current !== block) {
    if (isHiddenByStyle(current.getAttribute('style'))) return true
    current = current.parentElement
  }
  return false
}

const ownFontOf = (block: HTMLElement): BlockFont =>
  tallestRunFont(
    Array.from(block.querySelectorAll('[style]'))
      .filter((el) => !isHiddenWithin(el, block))
      .map((el) => fontDeclarationsOf(el.getAttribute('style'))),
  )

const pinBlocks = (editor: Editor, candidates: HTMLElement[]): void => {
  const blocks = candidates.filter(holdsInlineContent)
  const own = blocks.map(ownFontOf)
  const fonts = blockFontsToPin(own)
  blocks.forEach((block, i) => {
    const font = fonts[i]
    if (!font) return
    const styles: Record<string, string> = {}
    if (font.family) styles['font-family'] = font.family
    if (font.size) styles['font-size'] = font.size
    editor.dom.setStyles(block, styles)
  })
}

/**
 * After a paste, every block under `root` that holds text with a font of its
 * own takes the family and size of its tallest sized run, through the
 * editor's DOM so `data-mce-style` follows, so the strut and the words share
 * one font on every line of the text just added. A block whose text carries
 * no font of its own is left alone: what it sits inside styles its words and
 * its strut alike. Only an edit pins: the dynamic-text merge leaves a stored
 * box's blocks as its HTML names them.
 */
export const pinEditorBlockFonts = (
  editor: Editor,
  root: HTMLElement | null = editor.getBody(),
): void => {
  if (!root) return
  pinBlocks(
    editor,
    Array.from(root.querySelectorAll<HTMLElement>(INLINE_TEXT_BLOCK_SELECTOR)),
  )
}

/**
 * After a font command, the blocks the selection touches (and the blocks
 * inside them) are pinned the same way, and only those: a stored box's other
 * blocks keep the fonts its HTML names, so styling one line never repaints
 * the strut of the lines beside it.
 */
export const pinSelectedBlockFonts = (editor: Editor): void => {
  const selected = editor.selection.getSelectedBlocks() as HTMLElement[]
  const blocks = new Set<HTMLElement>()
  for (const block of selected) {
    if (block.matches(INLINE_TEXT_BLOCK_SELECTOR)) blocks.add(block)
    for (const inner of Array.from(
      block.querySelectorAll<HTMLElement>(INLINE_TEXT_BLOCK_SELECTOR),
    ))
      blocks.add(inner)
  }
  pinBlocks(editor, Array.from(blocks))
}
